import React, {
  FC,
  useEffect,
  useReducer,
  useRef,
  useContext,
  useState,
  useLayoutEffect,
  memo,
  useMemo,
  useCallback,
} from "react";
// import Select from "react-select";
// import { useLocation } from "react-router-dom";

import useFetch from "src/hooks/useFetch";
import { LoaderContext } from "src/context/loaderContext";
import { AuthContext } from "src/context/authContext";

import {
  ActionDashboardProps,
  ActionStatisticsDashboardActionType,
  PatientFilterState,
  PatientFilterStateActions,
  TodoResponseType,
} from "src/pages/actionDashboard/types";
import { tableFilterReducer } from "src/pages/actionDashboard/reducer";
import FooterText from "../../footerText";
import { isIoveraUser, isZilrettaUser } from "src/helpers";
import { format } from "date-fns";
import { start } from "repl";

/* -------------------------------------------------- */
/* CONSTANTS */
/* -------------------------------------------------- */

const DEFAULT_TAB = "coverage_available";

/* -------------------------------------------------- */
/* COMPONENT */
/* -------------------------------------------------- */

const ActionDashboard: FC<ActionDashboardProps> = ({
  apiEndpoint,
  userListEndpoint,
  initialState,
  tabs,
  FilterComponent,
  allowedRoles,
}) => {
  const [queryParams, dispatch] = useReducer(tableFilterReducer, initialState);
  const { setLoader, hideLoader, isVisible } = useContext(LoaderContext);
  const { userData } = useContext(AuthContext);
  // const location = useLocation();

  const [activeTab, setActiveTab] = useState<string>(DEFAULT_TAB);
  const firstFetch = useRef(true);

  /* -------------------------------------------------- */
  /* FETCH (MANUAL MODE) */
  /* -------------------------------------------------- */

  const {
    data: dashboardData,
    error: dashboardError,
    mutate: updateData,
    removeErrorAndData: clearDashboardData
  } = useFetch<TodoResponseType>(apiEndpoint, {}, {}, false);

  /* -------------------------------------------------- */
  /* INITIAL LOADER */
  /* -------------------------------------------------- */

  useLayoutEffect(() => {
    setLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --------------------------------------------------
    CHECK ON RESPONSE IF DATA IS AVAILABLE TO DISABLE THE LOADER
  -------------------------------------------------- */
  useEffect(() => {
    if (dashboardError || dashboardData) {
      if (isVisible) hideLoader();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardError, dashboardData])

  /* -------------------------------------------------- */
  /* FETCH HELPER (SINGLE SOURCE OF TRUTH) */
  /* -------------------------------------------------- */

  const fetchTabData = useCallback(
    (tabKey: string, extraParams: Partial<PatientFilterState> = {}) => {
      setLoader();
      console.log("TabKey....",tabKey,"sabfjvsadhvfhsd",queryParams.drug_id,"    ",extraParams.drug_id)
      if (!userData) return;

      const finalParams = {
        ...queryParams,
        ...extraParams,
        role: userData.ref_type,
        id: userData.id.toString(),
        section: tabKey as unknown as "coverage_available" | "action_required" | "pending" | "specialty_pharmacy" | "copay_claims",
        ...(tabKey !== queryParams.section ? {
          start_date: tabKey.includes("copay_claims")  ? format((new Date()).setDate((new Date()).getDate() - 90), "yyyy-MM-dd") : format((new Date()).setDate((new Date()).getDate() - 30), "yyyy-MM-dd"),

        } : {
        }),
        drug_id: isZilrettaUser(userData) ? "zilretta" : isIoveraUser(userData) ? "iovera°" : extraParams.drug_id ? extraParams.drug_id : "",      
};
      setLoader();
      clearDashboardData();
       dispatch({
      type: PatientFilterStateActions.SET_QUERY_PARAMS,
      payload: finalParams,
    });
      updateData({}, {}, finalParams);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryParams, updateData, userData, setLoader]
  );

  /* -------------------------------------------------- */
  /* DEFAULT FETCH (ONCE) */
  /* -------------------------------------------------- */

  useEffect(() => {
    if (userData && firstFetch.current) {
      firstFetch.current = false;
      fetchTabData(DEFAULT_TAB);
    }
  }, [userData, fetchTabData]);

  /* -------------------------------------------------- */
  /* HIDE LOADER */
  /* -------------------------------------------------- */

  useEffect(() => {
    if (dashboardData || dashboardError) {
      hideLoader();
    }
  }, [dashboardData, dashboardError, hideLoader]);

  /* -------------------------------------------------- */
  /* TAB HANDLER */
  /* -------------------------------------------------- */

  const handleTabClick = (tabKey: string) => {
    console.log("Tab Clicked....",tabKey,activeTab)
    if (tabKey !== activeTab) {
     
    const resetPages = resetAllPagesToFirst(tabKey);
    console.log("Reset Pages....",resetPages)
    dispatch({
      type: PatientFilterStateActions.SET_QUERY_PARAMS,
      payload: {
        ...queryParams,
        ...resetPages,
        start_date: tabKey.includes("copay_claims")  ? format((new Date()).setDate((new Date()).getDate() - 90), "yyyy-MM-dd") : format((new Date()).setDate((new Date()).getDate() - 30), "yyyy-MM-dd"),
        //drug_id:isZilrettaUser(userData) ? "zilretta": isIoveraUser(userData)?"iovera°": queryParams.drug_id ? queryParams.drug_id : "",
      },
    });
      setActiveTab(tabKey);
      fetchTabData(tabKey,{...resetPages, drug_id:queryParams.drug_id} );
    }
  };

  /* -------------------------------------------------- */
  /* PAGINATION */
  /* -------------------------------------------------- */

  const activeTabObj = tabs.find((t) => t.key === activeTab)!;

  const handlePageChange = useCallback(
    (pageObj: { selected: number }) => {
      console.log("Page Changed...", pageObj.selected,"    ",queryParams.drug_id);
      const action: ActionStatisticsDashboardActionType = {
        type: activeTabObj.changeAction as unknown as any,
        payload: pageObj.selected + 1,
      };

      dispatch(action);

      fetchTabData(activeTab, {
        [activeTabObj.pageKey]: pageObj.selected + 1,drug_id:queryParams.drug_id
      });
    },
    [activeTab, activeTabObj, fetchTabData]
  );

  const resetAllPagesToFirst = useCallback(
  (activeTabKey: string) => {
    const resetPages: Partial<PatientFilterState> = {};

    tabs.forEach((tab) => {
      (resetPages as any)[tab.pageKey] = 1;
    });

    // ensure active tab is also explicitly 1
    (resetPages as any)[
      tabs.find((t) => t.key === activeTabKey)!.pageKey
    ] = 1;

    return resetPages;
  },
  [tabs]
);
  /* -------------------------------------------------- */
  /* FILTER HANDLER */
  /* -------------------------------------------------- */

  const updateQueryParamsAndCallAPI = (updatedQueries: PatientFilterState) => {
    dispatch({
      type: PatientFilterStateActions.SET_QUERY_PARAMS,
      payload: updatedQueries,
    });
    console.log("updatedQueries.....",updatedQueries)
    fetchTabData(activeTab, updatedQueries);
  };

  /* -------------------------------------------------- */
  /* TABLE DATA */
  /* -------------------------------------------------- */

  const tableData = useMemo(
    () => activeTabObj.dataSelector(dashboardData?.data ?? {}),
    [dashboardData, activeTabObj]
  );

  /* -------------------------------------------------- */
  /* RENDER */
  /* -------------------------------------------------- */

  return (
    <section className="overflow-y-auto overflow-x-hidden d-flex flex-column flex-fill action-dashboard">
      <div className="container-fluid pt-3 d-flex flex-column flex-fill">
        <h2 className="page-heading mb-3">Action Dashboard</h2>

        {/* Filters */}
        <FilterComponent
          doctorDropdown={dashboardData?.data?.doctor_dropdown ?? []}
          practiceDropdown={dashboardData?.data?.practice_dropdown ?? []}
          region_dropdown={dashboardData?.data?.region_dropdown ?? []}
          territory_dropdown={dashboardData?.data?.territory_dropdown ?? []}
          state_dropdown={dashboardData?.data?.state_dropdown ?? []}
          defaultQueryParams={queryParams}
          updateQueryParams={updateQueryParamsAndCallAPI}
          activeTab={activeTabObj.key}
        />

        {/* Tabs */}
        <div className="nav nav-pills mb-3 mt-2 gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => handleTabClick(tab.key)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="card flex-fill">
          <div className="card-body p-0 d-flex flex-column">
            <activeTabObj.Component
              tableData={tableData}
              currentPage={queryParams[activeTabObj.pageKey]}
              onPageChange={handlePageChange}
              refreshListing={() => fetchTabData(activeTab)}
            />
          </div>
        </div>
      </div>

      <FooterText footerType="black" />
    </section>
  );
};

export default memo(ActionDashboard);
